import { ReactNode } from 'react'
import { AlertTriangle, ExternalLink, RotateCcw, Loader2 } from 'lucide-react'
import clsx from 'clsx'

interface ActionRequiredProps {
  /** O que está travado, em uma linha. */
  title: string
  /** Passos concretos que a pessoa precisa executar fora do sistema. */
  steps: ReactNode[]
  /** Link da ação (login, consentimento, portal de licenças). */
  link?: { href: string; label: string }
  /** Botão "Validei": re-checa o estado real em vez de confiar no que a pessoa disse. */
  onValidate?: () => void
  validating?: boolean
  validateLabel?: string
  /** Mensagem crua do erro que originou o bloqueio, quando houver. */
  detail?: string | null
  /** Conteúdo extra (ex: código de device login). */
  children?: ReactNode
  tone?: 'warning' | 'info'
}

/**
 * Painel de "ação humana necessária".
 *
 * Existe porque parte das travas da migração não se resolve por código — falta consentimento,
 * licença ou um login de admin. Antes essas travas apareciam como erro cru (ou como um botão
 * desabilitado sem explicação), e a pessoa precisava adivinhar o que fazer. Aqui a regra é:
 * dizer o passo, dar o link, e oferecer o "Validei" — que re-checa o estado de verdade.
 */
export function ActionRequired({
  title, steps, link, onValidate, validating, validateLabel = 'Validei — verificar',
  detail, children, tone = 'warning',
}: ActionRequiredProps) {
  const colors = tone === 'warning'
    ? { box: 'bg-amber-50 border-amber-200', icon: 'text-amber-600', title: 'text-amber-900', text: 'text-amber-800' }
    : { box: 'bg-blue-50 border-blue-200', icon: 'text-blue-600', title: 'text-blue-900', text: 'text-blue-800' }

  return (
    <div className={clsx('rounded-lg border p-4', colors.box)}>
      <div className="flex items-start gap-3">
        <AlertTriangle className={clsx('h-5 w-5 mt-0.5 shrink-0', colors.icon)} />
        <div className="flex-1 min-w-0">
          <h3 className={clsx('text-sm font-semibold', colors.title)}>{title}</h3>

          <ol className={clsx('mt-2 space-y-1 text-xs list-decimal list-inside', colors.text)}>
            {steps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>

          {children && <div className="mt-3">{children}</div>}

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {link && (
              <a
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-slate-800 hover:bg-slate-900 text-white"
              >
                <ExternalLink className="h-3 w-3" />
                {link.label}
              </a>
            )}
            {onValidate && (
              <button
                onClick={onValidate}
                disabled={validating}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
              >
                {validating
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <RotateCcw className="h-3 w-3" />}
                {validateLabel}
              </button>
            )}
          </div>

          {detail && (
            <p className="mt-2 text-[11px] font-mono text-slate-600 break-all">{detail}</p>
          )}
        </div>
      </div>
    </div>
  )
}
